import { useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import { Pdf16 } from '@carbon/icons-react';
import { RadarChart } from '@carbon/charts-react';
import {
  Button,
  CodeSnippetSkeleton,
  InlineLoading,
  TextAreaSkeleton,
} from 'carbon-components-react';
import { toJpeg } from 'html-to-image';
import { PDFDownloadLink } from '@react-pdf/renderer';
import Cookies from 'js-cookie';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { LangDefaults, useLang } from 'src/common/hooks/widgets/useLang';
import {
  urxScript,
  urxScriptLoader,
} from 'src/common/hooks/widgets/globalDefinitions';
import { FormModal } from 'src/common/utils/FormModal';
import {
  btnExport,
  location,
  object,
  pageTitle,
  pageUrl,
  productTitle,
  radarOptions,
  SKELETON_IMAGE,
  UT30N9J,
} from '../constants/default';
import './CyberResilienceAssessment.scss';
import 'src/common/style/common.scss';
import '@carbon/charts/styles.css';
import * as URX_FORM from '../constants/urx-form';
import countryList from '../data/currencies.json';
import Questions, { SectionArray } from './Questions/Questions';
import { useData } from '../hooks/useData';
import { segmentTracking } from '../lib/tracker';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import { PDFGenerate } from './PDFGenerate';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import PublicImage from 'src/common/components/PublicImage';
import cratImage from '../public/images/crat.png';
import { imageToBase64 } from '../lib/imageToBase64';
export interface CyberResilienceAssessmentProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
}

const CyberResilienceAssessment = ({
  langCode,
  test,
  countryToTest,
}: CyberResilienceAssessmentProps) => {
  const graphImage = 'images/screen-0.jpg';
  let localStorageFound = localStorage.getItem('langLocale');
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [urxlang] = useState(localeCode.toString());
  const [radarData, setRadarData] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [selectedSection, setSelectedSection] = useState<number>(0);
  const [sectionArray, setSectionArray] = useState<SectionArray[]>([]);
  const [, setCountry] = useState('');
  const [currency, setCurrency] = useState('');
  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );
  const [environment] = useEnv();
  const { bpName, logo } = useBPName(UT30N9J, true);
  const { assessment, pdfData } = useData(localeCode);
  const [chartImage, setChartImage] = useState(SKELETON_IMAGE);
  const [scorePercentage, setScorePercentage] = useState<number>(0);
  const [cratImageString, setBase64Image] = useState(SKELETON_IMAGE);
  const [loadInline, setLoadInline] = useState(false);
  const cookieURL = useCookiesURL();

  console.log('Your journey towards data', localeCode);
  if (localStorageFound == null)
    localStorageFound = LangDefaults.localCountryLang;

  window.urxEnvironment = environment;
  window.onUrxFormSubmitSuccess = () => {
    console.log('URX return triggered>>>>>>>');
    const element: HTMLElement = document.getElementsByClassName(
      'downloadLink',
    )[0] as HTMLElement;
    element.click();
    setDrawerOpen(false);
    setLoadInline(false);
  };

  const fetchImagesFromHTMLElement = async (id: string, bgColor: string) => {
    const div = document.getElementById(id) as HTMLElement;
    return toJpeg(div, { backgroundColor: bgColor });
  };

  useEffect(() => {
    if (cratImage) {
      imageToBase64(cratImage).then((base64String) => {
        setBase64Image(base64String);
      });
    }
    const initialState: any = [];
    assessment?.chartValues.map((category) =>
      initialState.push(
        {
          product: 'Ideal',
          feature: category,
          score: 90,
        },
        {
          product: 'User',
          feature: category,
          score: 0,
        },
      ),
    );
    setRadarData(initialState);
    return;
  }, [assessment?.chartValues]);

  useEffect(() => {
    if (!!sectionArray.length) {
      sectionArray.map((e) => {
        e.questions.map((j) => {
          const avg = j.ans.reduce((a, b) => a + b, 0) / j.quesCount;
          radarData.map((l: any) => {
            if (l.feature === j.questionName && l.product == 'User') {
              l.score = Math.round(avg * 16);
            }
          });
          return;
        });
      });
      setRadarData(radarData);
      setTimeout(() => {
        setHasChanged(false);
        setHasChanged(true);
      }, 1);
    }
  }, [sectionArray]);

  const props = {
    pdfData,
    radarData,
    chartImage,
    scorePercentage,
    cratImageString,
  };

  const pdfObject = PDFGenerate(props);

  const setCookie = () => {
    let radarNotes = '';
    let sum = 0;
    radarData
      .filter((e: any) => e.product == 'User')
      .map((l: any) => {
        let onePercent = 0;
        onePercent = (l.score * 100) / 90;
        sum += onePercent;
        radarNotes += ` ${l.feature}: ${l.score}`;
      });
    const overallScore = Math.round(sum / 5);
    setScorePercentage(overallScore);

    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: URX_FORM.FORM_ID,
        dcq: {
          Q_NOTESDATA:
            cookieURL +
            ' CRAT Results ' +
            ' |' +
            radarNotes +
            ' | Overall Assessment Score: ' +
            overallScore,
          Q_ASSET_URL: window.location.href,
          Q_ASSET_NAME: productTitle,
        },
      }),
    );
    console.log(Cookies.get('urxdcq'));
  };

  const getChartImage = async () => {
    setDrawerOpen(true);
    await fetchImagesFromHTMLElement('graphSummary', '#ffffff')
      .then((dataUrl) => {
        setChartImage(dataUrl);
        segmentTracking('CTA Clicked', {
          pageTitle: pageTitle,
          object: object,
          resultValue: 'CTA clicked',
          CTA: 'Download PDF',
          location: 'Assessment',
          action: '',
          field: '',
        });
        initUrxForm(
          environment,
          URX_FORM.INSTANCE_ID,
          URX_FORM.FORM_ID,
          urxlang as IBMLocale,
        );
        setCookie();
        setLoadInline(true);
      })
      .catch((error) => {
        console.error(
          'oops, something went wrong while fetching the images for results',
          error,
        );
      });
  };

  const fileName = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}_${minutes}`;
  };
  return (
    <div className="CRA_Light widget-styles">
      <Helmet>
        <script src={urxScript}></script>
        <script src={urxScriptLoader}></script>
      </Helmet>
      <div id="grid">
        <div className="bx--row mar3">
          <div
            className="bx--col-lg-10 bx--col-md-16 bx--col-sm-16 quesTable"
            no-toc="true"
          >
            {assessment?.sections && !!assessment?.sections.length ? (
              assessment?.sections.map((section: any, i) => (
                <Questions
                  key={i}
                  indexKey={i}
                  data={section}
                  locale={localeCode}
                  sectionArray={sectionArray}
                  setSectionArray={setSectionArray}
                  selectedSection={selectedSection}
                  onSectionChange={(e) => setSelectedSection(e)}
                />
              ))
            ) : (
              <>
                <CodeSnippetSkeleton
                  type="single"
                  style={{
                    marginBottom: 8,
                  }}
                />
                <CodeSnippetSkeleton type="multi" />
              </>
            )}
          </div>
          <div className="bx--col-lg-1 bx--col-md-16 bx--col-sm-16"></div>
          <div className="bx--col-lg-5 bx--col-md-16 bx--col-sm-16 graphSection">
            {assessment?.text_right ? (
              <div className="sticky">
                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                  <p>{assessment?.text_right}</p>
                </div>
                {hasChanged ? (
                  <>
                    <div
                      className="bx--col-lg-15 bx--col-md-15 bx--col-sm-15 screen"
                      id="graphSummary"
                    >
                      <RadarChart
                        data={radarData}
                        options={radarOptions}
                      ></RadarChart>
                    </div>
                    {assessment?.sections[selectedSection]?.subsection.map(
                      (sub: any, i) => (
                        <div
                          className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 bx--col-padding"
                          key={i}
                        >
                          <h5>{sub.questionName}</h5>
                          <p className="bx--col-padding pb-0">
                            {sub.featureDesc}
                          </p>
                        </div>
                      ),
                    )}
                    <div
                      className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 bx--col-padding"
                      id="graphSummary"
                    >
                      <Button
                        style={{ width: '100%' }}
                        renderIcon={Pdf16}
                        id="btn-pdf"
                        onClick={() => getChartImage()}
                        disabled={drawerOpen}
                      >
                        {drawerOpen ? (
                          <InlineLoading
                            status="active"
                            description={btnExport}
                          />
                        ) : (
                          btnExport
                        )}
                      </Button>
                      {drawerOpen && loadInline && (
                        <PDFDownloadLink
                          document={pdfObject}
                          fileName={`Resilience_Assessment_Report_${fileName()}.pdf`}
                          className="link downloadLink"
                        >
                          <p>Click me!</p>
                        </PDFDownloadLink>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="bx--col-lg-15 bx--col-md-15 bx--col-sm-15 screen">
                    <PublicImage
                      imgPath={graphImage}
                      widgetId="cyber-resilience-assessment"
                      width="100%"
                      height="100%"
                      alt="CRA description"
                    />
                  </div>
                )}
              </div>
            ) : (
              <TextAreaSkeleton />
            )}
          </div>
        </div>
        <FormModal
          pageTitle={pageTitle}
          object={object}
          productTitle={productTitle}
          pageUrl={pageUrl}
          location={location}
          bpName={bpName}
          logo={logo}
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          id={URX_FORM.INSTANCE_ID}
          formId={URX_FORM.FORM_ID}
          lang={lang}
        ></FormModal>
      </div>
    </div>
  );
};

export default CyberResilienceAssessment;
